import { showAlert } from '@silverfort/sf-components';
import { User } from '../../../types/User';
import { useDeleteUser, useUpdateUsers } from '../../../api';
import { useUserActionPopup, useUsersActionPopup } from './useUserActionPopup';

interface UseRemoveUserPopupParams {
    user: User;
    onUserRemoved?: () => void;
}

interface UseRemoveUsersPopupParams {
    users: User[];
    onUserRemoved?: () => void;
}

interface UseRemoveUserPopupReturnType {
    openRemoveUserPopup: () => void;
}

interface UseRemoveUsersPopupReturnType {
    openRemoveUsersPopup: () => void;
}

export const useRemoveUserPopup = ({ user, onUserRemoved }: UseRemoveUserPopupParams): UseRemoveUserPopupReturnType => {
    const handleSuccess = () => {
        closePopup();
        onUserRemoved?.();
    };

    const handleError = () => {
        closePopup();
        showAlert('Failed to remove user, please try again', 'error');
    };

    const { deleteUser } = useDeleteUser({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const { openUserActionPopup, closePopup } = useUserActionPopup({
        actionLabel: 'Remove',
        text: `Are you sure you want to remove ${user.userName}? This operation will not remove the user from Active Directory.`,
        user,
        onAction: () => deleteUser(user.guid),
        title: 'Remove user'
    });

    return {
        openRemoveUserPopup: openUserActionPopup
    };
};

export const useRemoveUsersPopup = ({
    users,
    onUserRemoved
}: UseRemoveUsersPopupParams): UseRemoveUsersPopupReturnType => {
    const handleSuccess = () => {
        closePopup();
        onUserRemoved?.();
    };

    const handleError = () => {
        closePopup();
        showAlert('Failed to remove users', 'error');
    };

    const { updateUsers } = useUpdateUsers({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const handleRemove = () => {
        updateUsers(users.map(({ guid }) => ({ guid, isManuallyRemoved: true })));
    };

    const { openUsersActionPopup, closePopup } = useUsersActionPopup({
        actionLabel: 'Remove',
        text: 'Are you sure you want to remove the selected users? This action will not remove the users from your Active Directory',
        users,
        onAction: handleRemove,
        title: 'Remove user'
    });

    return {
        openRemoveUsersPopup: openUsersActionPopup
    };
};
